import { useKnockTokenContext } from "@src/appV2/Knock/context";
import { registerPushTokenInKnock } from "@src/appV2/Knock/push/api";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
// TEMPORARY: Braze push sync — remove when legacy facility app is sunset
import { brazePushSyncRegisterToken } from "@src/appV2/NativeAdapters/brazePushSync.adapter";
import {
  addPushRegistrationErrorListener,
  addPushRegistrationListener,
  checkPushPermissions,
  type PushNotificationListenerHandle,
  registerPushNotifications,
  requestPushPermissions,
} from "@src/appV2/NativeAdapters/pushNotifications.adapter";
import { Platform } from "@src/mobile/utils/platform";
import { useCallback, useEffect, useRef, useState } from "react";

import { useUpdatePushToken } from "../api/useUpdatePushToken";
import { getStoredPushToken, shouldSendPushToken, storePushToken } from "../pushTokenStorage";

export type PushPermissionState = "unknown" | "granted" | "denied" | "prompt";

export interface UsePushNotificationRegistrationResult {
  /**
   * Current push permission state.
   * - "unknown": still checking
   * - "granted": user already granted permission
   * - "denied": user permanently denied permission
   * - "prompt": user hasn't been asked yet (or can be re-asked)
   */
  permissionState: PushPermissionState;
  /**
   * Call this to request push permissions and register for notifications.
   * Triggers the native OS permission dialog.
   */
  requestPermissionAndRegister: () => Promise<"granted" | "denied" | undefined>;
}

interface UsePushNotificationRegistrationParams {
  workplaceUserId: string;
  isAdmin: boolean;
}

export function usePushNotificationRegistration(
  params: UsePushNotificationRegistrationParams
): UsePushNotificationRegistrationResult {
  const { workplaceUserId, isAdmin } = params;
  const { mutate: updatePushToken } = useUpdatePushToken({ workplaceUserId });
  // KnockTokenProvider only mounts for non-admin workplace sessions. For
  // admin / pre-auth, useKnockTokenContext returns constate's empty sentinel
  // and `knockToken` resolves to undefined, so every Knock call below
  // short-circuits.
  const { knockToken } = useKnockTokenContext();
  // Latest-value ref: keeps handleToken's identity stable across JWT
  // refreshes, so the Capacitor push registration listeners aren't torn down
  // and re-registered every time the token rotates.
  const knockTokenRef = useRef(knockToken);
  knockTokenRef.current = knockToken;
  const listenersRef = useRef<PushNotificationListenerHandle[]>([]);
  const isMountedRef = useRef(true);
  const [permissionState, setPermissionState] = useState<PushPermissionState>("unknown");

  const handleToken = useCallback(
    (token: string) => {
      if (!workplaceUserId) {
        return;
      }

      // Knock register fires every time the OS push token does — outside the
      // shouldSendToken throttle that gates the backend PATCH. The Knock SDK
      // self-dedupes via read-modify-write append on the channel data.
      if (knockTokenRef.current) {
        void registerPushTokenInKnock({
          token,
          userId: workplaceUserId,
          knockToken: knockTokenRef.current,
        });
      }

      // TEMPORARY: Braze push sync — remove when legacy facility app is sunset
      // Register the push token with Braze's native SDK so it's associated
      // with the user set by changeUser, even when the token was already
      // cached by the platform push provider and onNewToken did not fire.
      if (Platform.isMobile) {
        void brazePushSyncRegisterToken(token).catch((error: unknown) => {
          logError(APP_V2_APP_EVENTS.BRAZE_PUSH_SYNC_FAILURE, { error });
        });
      }

      if (!shouldSendPushToken(workplaceUserId, token)) {
        return;
      }

      updatePushToken(
        { pushToken: token, platform: Platform.platform },
        {
          onSuccess: () => {
            storePushToken(workplaceUserId, token);
            logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_REGISTERED);
          },
        }
      );
    },
    [updatePushToken, workplaceUserId]
  );

  const setupListenersAndRegister = useCallback(
    async (options?: { isCancelled?: () => boolean }) => {
      const registrationListener = await addPushRegistrationListener((pushToken) => {
        handleToken(pushToken.value);
      });

      const errorListener = await addPushRegistrationErrorListener((error) => {
        logError(APP_V2_APP_EVENTS.PUSH_NOTIFICATION_REGISTRATION_ERROR, {
          error,
        });
      });

      if (options?.isCancelled?.()) {
        void registrationListener.remove();
        void errorListener.remove();
        return;
      }

      listenersRef.current = [registrationListener, errorListener];
      await registerPushNotifications();
    },
    [handleToken]
  );

  const requestPermissionAndRegister = useCallback(async (): Promise<
    "granted" | "denied" | undefined
  > => {
    if (!Platform.isMobile || !workplaceUserId || isAdmin) {
      return undefined;
    }

    try {
      logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_PERMISSION_REQUESTED);
      const permissionResult = await requestPushPermissions();

      if (permissionResult.receive === "granted") {
        logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_PERMISSION_GRANTED);
        setPermissionState("granted");
        await setupListenersAndRegister({ isCancelled: () => !isMountedRef.current });
        return "granted";
      } else if (permissionResult.receive === "denied") {
        logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_PERMISSION_DENIED);
        setPermissionState("denied");
        return "denied";
      } else {
        // User dismissed without choosing (e.g. Android "prompt-with-rationale")
        // Keep state as "prompt" so the soft prompt can re-appear after cooldown
        setPermissionState("prompt");
        return undefined;
      }
    } catch (error) {
      logError(APP_V2_APP_EVENTS.PUSH_NOTIFICATION_REGISTRATION_ERROR, {
        error,
      });
      return undefined;
    }
  }, [isAdmin, setupListenersAndRegister, workplaceUserId]);

  // Check existing permission state on mount (without prompting)
  useEffect(() => {
    if (!Platform.isMobile || !workplaceUserId || isAdmin) {
      return;
    }

    let isCancelled = false;

    void (async () => {
      try {
        const currentPermission = await checkPushPermissions();

        if (isCancelled) {
          return;
        }

        if (currentPermission.receive === "granted") {
          setPermissionState("granted");
          // Already granted — register silently
          await setupListenersAndRegister({ isCancelled: () => isCancelled });
        } else if (currentPermission.receive === "denied") {
          setPermissionState("denied");
        } else {
          // "prompt" or "prompt-with-rationale" — soft prompt should handle this
          setPermissionState("prompt");
        }
      } catch (error) {
        logError(APP_V2_APP_EVENTS.PUSH_NOTIFICATION_REGISTRATION_ERROR, {
          error,
        });
      }
    })();

    return () => {
      isCancelled = true;
      for (const listener of listenersRef.current) {
        void listener.remove();
      }
      listenersRef.current = [];
    };
  }, [isAdmin, setupListenersAndRegister, workplaceUserId]);

  // Late-JWT follow-up: when the Knock JWT becomes available *after* the OS
  // has already issued a push token (auth ready after device ready), register
  // the cached token with Knock once. The first-time `handleToken` path may
  // have run before the JWT existed — we don't want that token to be lost
  // until the next OS-level refresh.
  useEffect(() => {
    if (!Platform.isMobile || !workplaceUserId || !knockToken) {
      return;
    }

    const stored = getStoredPushToken(workplaceUserId);
    if (!stored) {
      return;
    }

    void registerPushTokenInKnock({
      token: stored.token,
      userId: workplaceUserId,
      knockToken,
    });
  }, [knockToken, workplaceUserId]);

  // Separate unmount-only effect for isMountedRef so it doesn't get
  // reset to false on every dependency change of the effect above.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { permissionState, requestPermissionAndRegister };
}
